import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { validateCheckoutSession } from '../../../api/checkout.api';
import { validateOngKey } from '../../../api/onglaye.api';
import backgroundImage from '../../../assets/background.jpg';
import CaptureRules from './components/CaptureRules';
import Footer from './components/Footer';
import RegisterPageBottomSection from './components/RegisterPageBottomSection';
import RegisterPageTopSection from './components/RegisterPageTopSection';

// OngLaye frontend URL for session expired redirect
const ONGLAYE_EXPIRED_URL =
  process.env.REACT_APP_ONGLAYE_URL || 'https://onglaye.dev.ayeface.com';

// Checkout frontend URL for session expired redirect
const CHECKOUT_EXPIRED_URL =
  process.env.REACT_APP_CHECKOUT_URL || 'https://checkout.dev.ayeface.com';

export default function LandingPage() {
  const [showCaptureRules, setShowCaptureRules] = useState(false);
  const [isOngLayeFlow, setIsOngLayeFlow] = useState(false);
  const [isCheckoutFlow, setIsCheckoutFlow] = useState(false);
  const [searchParams] = useSearchParams();

  // Get ong_key from URL params
  const ong_key = searchParams.get('ong_key');

  // Get checkout_session_id from URL params
  const checkout_session_id = searchParams.get('checkout_session_id');

  // Use TanStack Query to validate ong_key
  const {
    data: ongKeyValidation,
    isLoading: isValidatingOngKey,
    isError: ongKeyError
  } = useQuery({
    queryKey: ['onglaye-validate', ong_key],
    queryFn: () => validateOngKey(ong_key!),
    enabled: !!ong_key, // Only run if ong_key is present
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  });

  // Use TanStack Query to validate checkout_session_id
  const {
    data: checkoutValidation,
    isLoading: isValidatingCheckout,
    isError: checkoutError
  } = useQuery({
    queryKey: ['checkout-validate', checkout_session_id],
    queryFn: () => validateCheckoutSession(checkout_session_id!),
    enabled: !!checkout_session_id && !ong_key, // Only run if checkout_session_id is present and no ong_key
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  });

  // Handle ong_key validation result
  useEffect(() => {
    if (!ong_key || isValidatingOngKey) return;

    if (ongKeyValidation?.success) {
      // Store OngLaye session data for later use
      sessionStorage.setItem('ong_key', ong_key);
      sessionStorage.setItem('is_onglaye_flow', 'true');
      if (ongKeyValidation.bizToken) {
        sessionStorage.setItem('onglaye_biz_token', ongKeyValidation.bizToken);
      }
      if (ongKeyValidation.faceIndex) {
        sessionStorage.setItem(
          'onglaye_face_index',
          ongKeyValidation.faceIndex
        );
      }

      // Mark as OngLaye flow and show CaptureRules
      setIsOngLayeFlow(true);
      setShowCaptureRules(true);
    } else if (ongKeyValidation && !ongKeyValidation.success) {
      // Invalid ong_key - redirect to OngLaye session expired page
      console.error(
        'OngLaye ong_key validation failed:',
        ongKeyValidation.error
      );
      window.location.href = `${ONGLAYE_EXPIRED_URL}/expired`;
    }
  }, [ong_key, ongKeyValidation, isValidatingOngKey]);

  // Handle checkout_session_id validation result
  useEffect(() => {
    if (!checkout_session_id || isValidatingCheckout || ong_key) return;

    if (checkoutValidation?.success) {
      // Store checkout session data for later use
      sessionStorage.setItem('checkout_session_id', checkout_session_id);
      sessionStorage.setItem('is_checkout_flow', 'true');
      if (checkoutValidation.bizToken) {
        sessionStorage.setItem(
          'checkout_biz_token',
          checkoutValidation.bizToken
        );
      }
      if (checkoutValidation.faceIndex) {
        sessionStorage.setItem(
          'checkout_face_index',
          checkoutValidation.faceIndex
        );
      }
      // First remove to set new values in case user is coming from a different checkout session with different prefill data
      sessionStorage.removeItem('external_prefill_phone_number');
      sessionStorage.removeItem('external_prefill_country_code');
      sessionStorage.removeItem('external_prefill_phone_verification_token');
      // Pre-fill phone from similar-face OTP verification
      if (checkoutValidation.prefill?.phone_number) {
        sessionStorage.setItem(
          'external_prefill_phone_number',
          checkoutValidation.prefill.phone_number
        );
        sessionStorage.setItem(
          'external_prefill_country_code',
          checkoutValidation.prefill.country_code
        );
      }
      // Phone already verified — skip OTP in registration form
      if (checkoutValidation.phoneVerificationToken) {
        sessionStorage.setItem(
          'external_prefill_phone_verification_token',
          checkoutValidation.phoneVerificationToken
        );
      }
      // Store payment_required flag so RegisterScreen can skip payment notes
      sessionStorage.setItem(
        'checkout_payment_required',
        String(checkoutValidation.payment_required ?? true)
      );

      // Mark as checkout flow and show CaptureRules
      setIsCheckoutFlow(true);
      setShowCaptureRules(true);
    } else if (checkoutValidation && !checkoutValidation.success) {
      // Invalid checkout session - redirect to checkout expired page
      console.error(
        'Checkout session validation failed:',
        checkoutValidation.error
      );
      window.location.href = `${CHECKOUT_EXPIRED_URL}/expired`;
    }
  }, [checkout_session_id, checkoutValidation, isValidatingCheckout, ong_key]);

  // Handle query error
  useEffect(() => {
    if (ongKeyError && ong_key) {
      console.error('Error validating ong_key');
      window.location.href = `${ONGLAYE_EXPIRED_URL}/expired`;
    }
  }, [ongKeyError, ong_key]);

  // Handle checkout query error
  useEffect(() => {
    if (checkoutError && checkout_session_id) {
      console.error('Error validating checkout session');
      window.location.href = `${CHECKOUT_EXPIRED_URL}/expired`;
    }
  }, [checkoutError, checkout_session_id]);

  // Show loading while validating OngLaye key or checkout session
  if (
    (isValidatingOngKey && ong_key) ||
    (isValidatingCheckout && checkout_session_id)
  ) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center bg-white"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#5CB891] border-t-transparent"></div>
          <p className="text-lg font-medium text-gray-700">
            Validating your session...
          </p>
        </div>
      </div>
    );
  }

  return showCaptureRules ? (
    <CaptureRules
      isOngLayeFlow={isOngLayeFlow}
      isCheckoutFlow={isCheckoutFlow}
      paymentRequired={
        // for non-checkout flows this will be ignored, default true
        checkoutValidation?.success
          ? (checkoutValidation?.payment_required ?? true)
          : true
      }
    />
  ) : (
    <div
      className="flex flex-col items-center bg-white"
      style={
        showCaptureRules
          ? {}
          : {
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              minHeight: '100vh'
            }
      }
    >
      <div className="flex flex-col items-center justify-center">
        <RegisterPageTopSection />
      </div>
      <RegisterPageBottomSection
        onShowCaptureRules={() => setShowCaptureRules(true)}
      />
      <Footer />
    </div>
  );
}
